<!-- src/pages/RecipeView.vue -->
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_RECIPE_BY_SLUG } from '../graphql/queries'
import { DELETE_RECIPE } from '../graphql/mutations'
import { showError, showSuccess } from '../utils/errorHandling'
import { formatDate } from '../utils/dateHelpers'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import EditRecipeModal from '../components/EditRecipeModal.vue'
import CreateMealModal from '../components/CreateMealModal.vue'

const router = useRouter()
const route = useRoute()

// State
const showDeleteDialog = ref(false)
const showEditModal = ref(false)
const showMealModal = ref(false)
const activeTab = ref('overview') // overview, ingredients

const getInitials = (title: string) =>
  title.split(' ').filter(w => w && /^[A-ZÀ-Ú]/u.test(w)).map(w => w[0].toUpperCase()).slice(0, 3).join('')

// Get recipe slug from route params
const recipeSlug = computed(() => route.params.slug as string)

// GraphQL queries
const { result: recipeResult, loading: loadingRecipe, refetch: refetchRecipe } = useQuery(
  GET_RECIPE_BY_SLUG,
  () => ({ slug: recipeSlug.value }),
  { enabled: !!recipeSlug.value, fetchPolicy: 'no-cache' }
)

// Delete mutation
const { mutate: deleteRecipeMutation, loading: deletingRecipe } = useMutation(DELETE_RECIPE)

// Computed properties
const recipe = computed(() => recipeResult.value?.recipeBySlug || null)

const categoryBadgeClass = computed(() => {
  if (!recipe.value?.category?.name) return 'bg-gray-500 text-white'

  const categoryName = recipe.value.category.name.toLowerCase()
  const categoryStyles = {
    vegetables: 'category-vegetables',
    vegetable: 'category-vegetables',
    'main dishes': 'category-main-dishes',
    'main dish': 'category-main-dishes',
    main: 'category-main-dishes',
    fish: 'category-fish',
    seafood: 'category-fish',
    breakfast: 'category-breakfast',
    dessert: 'category-breakfast'
  }

  return categoryStyles[categoryName as keyof typeof categoryStyles] || 'category-main-dishes'
})

const categoryDotClass = computed(() => {
  if (!recipe.value?.category?.name) return 'bg-gray-500'

  const categoryName = recipe.value.category.name.toLowerCase()
  const dotStyles = {
    vegetables: 'bg-green-500',
    vegetable: 'bg-green-500',
    'main dishes': 'bg-orange-500',
    'main dish': 'bg-orange-500',
    main: 'bg-orange-500',
    fish: 'bg-blue-500',
    seafood: 'bg-blue-500',
    breakfast: 'bg-yellow-400',
    dessert: 'bg-yellow-400'
  }

  return dotStyles[categoryName as keyof typeof dotStyles] || 'bg-orange-500'
})

const formatCategoryName = (categoryName: string | undefined) => {
  if (!categoryName) return 'Unknown'

  const categoryNames = {
    vegetables: 'Vegetables',
    vegetable: 'Vegetables',
    'main dishes': 'Main Dishes',
    'main dish': 'Main Dishes',
    main: 'Main Dishes',
    fish: 'Fish',
    seafood: 'Fish',
    breakfast: 'Breakfast',
    dessert: 'Dessert'
  }

  return categoryNames[categoryName.toLowerCase() as keyof typeof categoryNames] || categoryName
}

// Methods
const goBack = () => {
  router.push('/recipes')
}

const editRecipe = () => {
  showEditModal.value = true
}

const onRecipeUpdated = (updatedRecipe: any) => {
  showEditModal.value = false
  refetchRecipe()
  showSuccess(`Recipe "${updatedRecipe.title}" updated successfully!`)
}

const deleteRecipe = () => {
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!recipe.value) return

  try {
    await deleteRecipeMutation({ id: recipe.value.id })
    showDeleteDialog.value = false
    showSuccess('Recipe deleted successfully!')
    router.push('/recipes')
  } catch (error) {
    showError(error, 'delete recipe')
  }
}

const cancelDelete = () => {
  showDeleteDialog.value = false
}

</script>

<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Loading State -->
    <div v-if="loadingRecipe" class="max-w-4xl mx-auto">
      <div class="card-elevated overflow-hidden">
        <div class="skeleton h-64"></div>
        <div class="spacing-lg">
          <div class="skeleton-text mb-4 h-8"></div>
          <div class="skeleton-text mb-6 w-3/4 h-6"></div>
          <div class="flex gap-3 mb-6">
            <div class="skeleton h-10 w-24"></div>
            <div class="skeleton h-10 w-24"></div>
          </div>
          <div class="skeleton h-32"></div>
        </div>
      </div>
    </div>

    <!-- Recipe Not Found -->
    <div v-else-if="!recipe" class="error-state">
      <div class="text-6xl mb-4">😕</div>
      <h3 class="text-section-header mb-2">Recipe not found</h3>
      <p class="text-caption mb-6">The recipe you're looking for doesn't exist or has been deleted.</p>
      <button @click="goBack" class="btn-primary">
        Back to Recipes
      </button>
    </div>

    <!-- Recipe Content -->
    <div v-else class="space-y-8">
      <!-- Header -->
      <div class="card-elevated overflow-hidden">
        <!-- Recipe Image -->
        <div class="h-64 flex items-center justify-center relative recipe-hero-bg">
          <span class="font-bold select-none recipe-hero-initials">
            {{ getInitials(recipe.title) }}
          </span>
          <!-- Back Button -->
          <button @click="goBack"
            class="absolute top-4 left-4 p-3 bg-white/90 backdrop-blur-sm text-gray-700 rounded-full hover:bg-white transition-all"
            title="Back to recipes">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <!-- Category badge -->
          <div class="absolute top-4 right-4">
            <span class="category-badge" :class="categoryBadgeClass">
              {{ formatCategoryName(recipe.category?.name) }}
            </span>
          </div>
        </div>

        <!-- Recipe Header Info -->
        <div class="spacing-lg">
          <div class="mb-6">
            <h1 class="text-4xl font-bold text-gray-900 mb-3">{{ recipe.title }}</h1>
            <p v-if="recipe.description" class="text-body text-lg">{{ recipe.description }}</p>
          </div>

          <!-- Action Buttons Row -->
          <div class="flex gap-3 mb-6">
            <button @click="showMealModal = true"
              class="flex items-center gap-2 rounded-lg font-semibold text-white transition-all hover:opacity-90 recipe-plan-btn">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Plan Meal
            </button>
            <button @click="editRecipe" class="btn-secondary flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                </path>
              </svg>
              Edit Recipe
            </button>
            <button @click="deleteRecipe" class="btn-secondary text-red-600 hover:bg-red-50 hover:text-red-700">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                </path>
              </svg>
              Delete
            </button>
          </div>

          <!-- Recipe Meta Info -->
          <div class="bg-gray-50 rounded-lg spacing-md">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex justify-between items-center">
                <span class="text-label">Category:</span>
                <span class="category-badge" :class="categoryBadgeClass">
                  {{ formatCategoryName(recipe.category?.name) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-label">Ingredients:</span>
                <span class="text-body font-medium">{{ recipe.ingredients?.length || 0 }} items</span>
              </div>
              <div class="flex justify-between">
                <span class="text-label">Created:</span>
                <span class="text-caption">{{ formatDate(recipe.created_at) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-label">Updated:</span>
                <span class="text-caption">{{ formatDate(recipe.updated_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop: two-column layout -->
      <div class="hidden lg:flex gap-6 items-start">
        <!-- Overview — 9/12 -->
        <div class="card-elevated flex-1 min-w-0 spacing-lg">
          <h3 class="text-section-header mb-4">Instructions</h3>
          <div v-if="recipe.content" class="prose prose-slate max-w-none" v-html="recipe.content" />
          <div v-else class="text-center py-10 text-gray-400 text-sm">No instructions added yet.</div>
        </div>

        <!-- Ingredients — 3/12 -->
        <div class="card-elevated w-72 flex-shrink-0 spacing-lg">
          <h3 class="text-section-header mb-4">Ingredients</h3>
          <div v-if="recipe.ingredients && recipe.ingredients.length > 0" class="space-y-2">
            <div v-for="ingredient in recipe.ingredients" :key="ingredient.id"
              class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full flex-shrink-0" :class="categoryDotClass"></div>
                <span class="text-body font-medium text-sm">{{ ingredient.name }}</span>
              </div>
              <span class="text-caption text-sm font-semibold ml-2 whitespace-nowrap">
                {{ ingredient.quantity }} {{ ingredient.unit || '' }}
              </span>
            </div>
          </div>
          <div v-else class="text-center py-6 text-gray-400 text-sm">No ingredients listed.</div>
        </div>
      </div>

      <!-- Mobile/narrow: tabs -->
      <div class="lg:hidden card-elevated">
        <div class="border-b border-gray-200">
          <nav class="-mb-px flex space-x-8 px-6">
            <button v-for="tab in [
              { id: 'overview', name: 'Overview', icon: '📋' },
              { id: 'ingredients', name: 'Ingredients', icon: '🥕' }
            ]" :key="tab.id" @click="activeTab = tab.id" :class="`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === tab.id
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`">
              <span>{{ tab.icon }}</span>
              {{ tab.name }}
            </button>
          </nav>
        </div>
        <div class="spacing-lg">
          <div v-if="activeTab === 'overview'">
            <div v-if="recipe.content" class="prose prose-slate max-w-none" v-html="recipe.content" />
            <div v-else class="text-center py-10 text-gray-400 text-sm">No instructions added yet.</div>
          </div>
          <div v-else-if="activeTab === 'ingredients'">
            <h3 class="text-section-header mb-6">Ingredients</h3>
            <div v-if="recipe.ingredients && recipe.ingredients.length > 0" class="space-y-3">
              <div v-for="ingredient in recipe.ingredients" :key="ingredient.id"
                class="flex items-center justify-between spacing-md bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div class="flex items-center space-x-3">
                  <div class="w-3 h-3 rounded-full" :class="categoryDotClass"></div>
                  <span class="text-body font-medium">{{ ingredient.name }}</span>
                </div>
                <div class="text-right">
                  <div class="text-body font-semibold">{{ ingredient.quantity }} {{ ingredient.unit || '' }}</div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <div class="text-4xl mb-2">🥕</div>
              <p class="text-caption">No ingredients listed for this recipe.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Plan Meal Modal -->
    <CreateMealModal :show="showMealModal" :preselected-recipe="recipe" @close="showMealModal = false"
      @created="showMealModal = false" />

    <!-- Edit Recipe Modal -->
    <EditRecipeModal v-if="recipe" :show="showEditModal" :recipe="recipe" @close="showEditModal = false"
      @updated="onRecipeUpdated" />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog :show="showDeleteDialog" title="Delete Recipe"
      :message="recipe ? `Are you sure you want to delete &quot;${recipe.title}&quot;? This action cannot be undone.` : ''"
      confirm-text="Delete" :loading="deletingRecipe" @confirm="confirmDelete" @cancel="cancelDelete" />
  </div>
</template>

<style scoped>
/* ── Recipe hero placeholder ── */
.recipe-hero-bg      { background: #CADBC7; }
.recipe-hero-initials { font-size: 80px; letter-spacing: 0.03em; color: #3F4A3C; }
.recipe-plan-btn     { background: #4F8646; padding: 10px 20px; font-size: 14px; }

/* Force list markers that Tailwind preflight strips */
:deep(.prose ol) {
  list-style-type: decimal !important;
  padding-left: 1.75rem !important;
}

:deep(.prose ul) {
  list-style-type: disc !important;
  padding-left: 1.75rem !important;
}

:deep(.prose li) {
  display: list-item !important;
}

/* Tiptap wraps li text in <p> — collapse the extra margin */
:deep(.prose li > p) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
</style>
